import { z } from 'zod';
import { IsoDateTimeSchema } from './common.js';
import { OperatorDeviceCommandTypeSchema } from './screen.js';
/**
 * Bulk device commands: one operator action against a screen group,
 * tracked as a batch of ordinary individual device commands.
 *
 * Deliberately absent: any single "batch succeeded" field. A batch can
 * finish with a mix of executed, failed, expired and excluded recipients,
 * and the API must never offer a caller a way to collapse that into one
 * green state. Callers read the counts.
 */
export const CreateDeviceCommandBatchRequestSchema = z.object({
    screenGroupId: z.string().uuid(),
    type: OperatorDeviceCommandTypeSchema,
    payload: z.record(z.unknown()).optional(),
});
/**
 * Why a snapshotted recipient did not receive a command. Exclusions are
 * always reported, never silently omitted from the batch.
 */
export const BatchExclusionReasonSchema = z.enum([
    'no_paired_device',
    'device_revoked',
    'command_not_supported',
    'duplicate_active_command',
]);
/**
 * Derived from the individual commands on every read -- never stored
 * counters. total counts every snapshotted recipient, so
 * total = excluded + pending + sent + acknowledged + executed + failed + expired.
 */
export const BatchStatusCountsSchema = z.object({
    total: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    acknowledged: z.number().int().nonnegative(),
    executed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    expired: z.number().int().nonnegative(),
});
/**
 * screenGroupId is nullable because a group may be deleted after the batch
 * ran; screenGroupName is an immutable snapshot taken at creation, so batch
 * history stays readable regardless of what happens to the group later.
 */
export const DeviceCommandBatchSchema = z.object({
    id: z.string().uuid(),
    organisationId: z.string().uuid(),
    screenGroupId: z.string().uuid().nullable(),
    screenGroupName: z.string(),
    commandType: OperatorDeviceCommandTypeSchema,
    issuedByUserId: z.string().uuid().nullable(),
    issuedByDisplayName: z.string().nullable(),
    recipientCount: z.number().int().nonnegative(),
    excludedCount: z.number().int().nonnegative(),
    createdAt: IsoDateTimeSchema,
});
/** `created` distinguishes a new batch (201) from an idempotent replay (200). */
export const CreateDeviceCommandBatchResponseSchema = DeviceCommandBatchSchema.extend({
    counts: BatchStatusCountsSchema,
    created: z.boolean(),
});
export const DeviceCommandBatchStatusSchema = DeviceCommandBatchSchema.extend({
    counts: BatchStatusCountsSchema,
});
/**
 * deviceId is nullable, either because the device was deleted since or
 * because the screen had no paired device when the batch was created;
 * screenName is the snapshot that keeps the row identifiable in both cases.
 * A recipient has EITHER a command (with its own lifecycle fields) OR an
 * excludedReason -- never both.
 */
export const BatchRecipientSchema = z.object({
    id: z.string().uuid(),
    deviceId: z.string().uuid().nullable(),
    deviceCode: z.string().nullable(),
    screenId: z.string().uuid().nullable(),
    /** Immutable name snapshot: how the operator identifies this row, paired or not. */
    screenName: z.string().nullable(),
    commandId: z.string().uuid().nullable(),
    commandStatus: z.string().nullable(),
    excludedReason: BatchExclusionReasonSchema.nullable(),
    resultMessage: z.string().nullable(),
    createdAt: IsoDateTimeSchema.nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
});
export const BatchRecipientsResponseSchema = z.object({
    items: z.array(BatchRecipientSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=device-command-batch.js.map